import { useState } from "react"
import { useAuth } from '@/auth/context/AuthContext'
import { useDocumentTitle } from '@/shared/hooks/use-document-title'
import { authApi } from '@/shared/services/auth'
import { EditFieldDialog } from '@/modules/account/components/EditFieldDialog'
import { ChangePasswordDialog } from '@/modules/account/components/ChangePasswordDialog'
import { Button } from '@/shared/components/ui/button'
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/components/ui/avatar'
import { Separator } from '@/shared/components/ui/separator'
import { Switch } from '@/shared/components/ui/switch'
import {
  ChevronRight,
  Shield,
  Bell,
  Moon,
  Globe,
  UserCircle,
  Settings,
  Activity,
  LogOut,
  Users,
} from "lucide-react"

interface EditField {
  field: string
  label: string
  value: string
}

export function AccountPage() {
  useDocumentTitle("Mi Cuenta")
  const { user, checkAuth, logout } = useAuth()

  const [editField, setEditField] = useState<EditField | null>(null)
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false)

  if (!user) return null

  const initials = `${user.first_name?.[0] || ""}${user.last_name?.[0] || ""}`.toUpperCase() || user.email[0].toUpperCase()
  const fullName = user.first_name && user.last_name
    ? `${user.first_name} ${user.last_name}`
    : user.email.split("@")[0]

  const handleEditField = (field: string, label: string, value: string) => {
    setEditField({ field, label, value })
  }

  const handleSaveField = async (value: string) => {
    if (!editField) return

    const data: Record<string, string> = {}
    data[editField.field] = value

    const response = await authApi.updateProfile(data)

    if (!response.success) {
      throw new Error(response.message)
    }

    await checkAuth()
  }

  const handlePasswordSuccess = async () => {
    await checkAuth()
  }

  const memberSince = new Date(user.date_joined).toLocaleDateString("es-ES", {
    year: "numeric",
    month: "long",
  })

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Settings className="h-6 w-6 text-muted-foreground" />
          <h1 className="text-2xl font-semibold tracking-tight">Configuración</h1>
        </div>
        <div className="flex items-center gap-4">
          <p className="text-sm text-muted-foreground">
            Administra tu cuenta y preferencias
          </p>
          <Button
            variant="ghost"
            size="icon"
            className="text-muted-foreground hover:text-destructive"
            onClick={() => logout()}
          >
            <LogOut className="h-5 w-5" />
          </Button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_280px]">
        {/* Left Column - Settings */}
        <div className="space-y-6">
          {/* Personal Information */}
          <SettingsSection
            icon={<UserCircle className="h-5 w-5" />}
            title="Información personal"
            description="Actualiza tu información de perfil"
          >
            <SettingsRow
              label="Nombre"
              value={user.first_name || "—"}
              onClick={() => handleEditField("first_name", "Nombre", user.first_name || "")}
            />
            <SettingsRow
              label="Apellido"
              value={user.last_name || "—"}
              onClick={() => handleEditField("last_name", "Apellido", user.last_name || "")}
            />
            <SettingsRow
              label="Correo electrónico"
              value={user.email}
              disabled
            />
            <SettingsRow
              label="Teléfono"
              value={user.telefono || "—"}
              onClick={() => handleEditField("telefono", "Teléfono", user.telefono || "")}
              isLast
            />
          </SettingsSection>

          {/* Security */}
          <SettingsSection
            icon={<Shield className="h-5 w-5" />}
            title="Seguridad"
            description="Protege tu cuenta"
            alert={!user.password_changed_once}
          >
            <SettingsRow
              label="Contraseña"
              description={!user.password_changed_once ? "Crea una contraseña personalizada para mayor seguridad" : undefined}
              value="••••••••"
              onClick={() => setPasswordDialogOpen(true)}
            />
            <SettingsRow
              label="Autenticación de dos factores"
              value="No configurado"
              muted
              isLast
            />
          </SettingsSection>

          {/* Preferences */}
          <SettingsSection
            icon={<Settings className="h-5 w-5" />}
            title="Preferencias"
            description="Personaliza tu experiencia"
          >
            <SettingsToggleRow
              label="Notificaciones por correo"
              description="Recibe alertas sobre tu cuenta"
              icon={<Bell className="h-4 w-4" />}
              defaultChecked={true}
              disabled
            />
            <SettingsToggleRow
              label="Modo oscuro"
              description="Cambia la apariencia de la interfaz"
              icon={<Moon className="h-4 w-4" />}
              defaultChecked={false}
              disabled
            />
            <SettingsRow
              label="Idioma"
              value="Español"
              icon={<Globe className="h-4 w-4" />}
              isLast
            />
          </SettingsSection>

          {/* Activity */}
          <SettingsSection
            icon={<Activity className="h-5 w-5" />}
            title="Actividad"
            description="Información de tu cuenta"
          >
            <div className="grid grid-cols-3 gap-4 p-4">
              <div className="text-center">
                <p className="text-2xl font-semibold">1</p>
                <p className="text-xs text-muted-foreground">Sesión activa</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-semibold">
                  {user.last_login
                    ? new Date(user.last_login).toLocaleDateString("es-ES", { day: "2-digit", month: "short" })
                    : "—"
                  }
                </p>
                <p className="text-xs text-muted-foreground">Último acceso</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-semibold">
                  {Math.floor((Date.now() - new Date(user.date_joined).getTime()) / (1000 * 60 * 60 * 24))}
                </p>
                <p className="text-xs text-muted-foreground">Días activo</p>
              </div>
            </div>
          </SettingsSection>
        </div>

        {/* Right Column - Profile Card */}
        <div className="space-y-6">
          {/* Profile Info */}
          <div className="rounded-xl border bg-card p-6">
            <div className="flex flex-col items-center text-center">
              <Avatar className="h-20 w-20 mb-4">
                {user.avatar_url && <AvatarImage src={user.avatar_url} alt={fullName} />}
                <AvatarFallback className="text-xl bg-primary text-primary-foreground">
                  {initials}
                </AvatarFallback>
              </Avatar>
              <h2 className="font-semibold text-lg">{fullName}</h2>
              <p className="text-sm text-muted-foreground">{user.email}</p>
              <div className="mt-3 inline-flex items-center rounded-full bg-muted px-3 py-1 text-xs">
                {user.display_group || user.groups[0] || "Sin grupo"}
              </div>
            </div>

            <Separator className="my-5" />

            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Estado</span>
                <span className="flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-full bg-green-500" />
                  Activo
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Miembro desde</span>
                <span>{memberSince}</span>
              </div>
            </div>
          </div>

          {/* Groups */}
          <div className="rounded-xl border bg-card p-5">
            <div className="flex items-center gap-2 mb-4">
              <Users className="h-4 w-4 text-muted-foreground" />
              <h3 className="font-medium text-sm">Grupos asignados</h3>
              <span className="ml-auto text-xs text-muted-foreground">{user.groups_info?.length || user.groups.length}</span>
            </div>
            <div className="space-y-2 max-h-[280px] overflow-y-auto pr-1">
              {(user.groups_info?.length > 0 || user.groups.length > 0) ? (
                [...(user.groups_info || user.groups.map(g => ({ name: g, is_laboratorio: false })))].sort((a, b) => a.name.localeCompare(b.name)).map((group, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-2 text-sm py-1.5 px-2 rounded-md bg-muted/50"
                  >
                    <span className="truncate">{group.name}</span>
                    {group.is_laboratorio && (
                      <span className="ml-auto text-[10px] text-blue-700 font-medium">LAB</span>
                    )}
                  </div>
                ))
              ) : (
                <p className="text-sm text-muted-foreground">Sin grupos asignados</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Dialogs */}
      {editField && (
        <EditFieldDialog
          open={!!editField}
          onOpenChange={(open) => !open && setEditField(null)}
          field={editField.field}
          label={editField.label}
          currentValue={editField.value}
          onSave={handleSaveField}
        />
      )}

      <ChangePasswordDialog
        open={passwordDialogOpen}
        onOpenChange={setPasswordDialogOpen}
        onSuccess={handlePasswordSuccess}
      />
    </div>
  )
}

// Settings Section Component
function SettingsSection({
  icon,
  title,
  description,
  alert,
  children,
}: {
  icon: React.ReactNode
  title: string
  description: string
  alert?: boolean
  children: React.ReactNode
}) {
  return (
    <div className="rounded-xl border bg-card overflow-hidden">
      <div className="flex items-center gap-3 px-5 py-4 border-b bg-muted/30">
        <span className="text-muted-foreground">{icon}</span>
        <div className="flex-1">
          <h3 className="font-medium">{title}</h3>
          <p className="text-xs text-muted-foreground">{description}</p>
        </div>
        {alert && (
          <span className="flex h-2 w-2 rounded-full bg-amber-500" />
        )}
      </div>
      <div>{children}</div>
    </div>
  )
}

// Settings Row Component
function SettingsRow({
  label,
  description,
  value,
  icon,
  disabled,
  muted,
  isLast,
  onClick,
}: {
  label: string
  description?: string
  value: string
  icon?: React.ReactNode
  disabled?: boolean
  muted?: boolean
  isLast?: boolean
  onClick?: () => void
}) {
  const content = (
    <>
      <div className="flex items-center gap-3">
        {icon && <span className="text-muted-foreground">{icon}</span>}
        <div>
          <span className={`text-sm ${muted ? "text-muted-foreground" : ""}`}>{label}</span>
          {description && (
            <p className="text-xs text-amber-600 mt-0.5">{description}</p>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground">
          {value}
        </span>
        {onClick && !disabled && <ChevronRight className="h-4 w-4 text-muted-foreground" />}
      </div>
    </>
  )

  const className = `flex items-center justify-between px-5 py-3.5 ${!isLast ? "border-b" : ""} ${
    onClick && !disabled ? "hover:bg-muted/50 cursor-pointer transition-colors" : ""
  }`

  if (onClick && !disabled) {
    return (
      <button onClick={onClick} className={`${className} w-full text-left`}>
        {content}
      </button>
    )
  }

  return <div className={className}>{content}</div>
}

// Settings Toggle Row Component
function SettingsToggleRow({
  label,
  description,
  icon,
  defaultChecked,
  disabled,
  isLast,
}: {
  label: string
  description?: string
  icon?: React.ReactNode
  defaultChecked?: boolean
  disabled?: boolean
  isLast?: boolean
}) {
  return (
    <div className={`flex items-center justify-between px-5 py-3.5 ${!isLast ? "border-b" : ""}`}>
      <div className="flex items-center gap-3">
        {icon && <span className="text-muted-foreground">{icon}</span>}
        <div>
          <p className="text-sm">{label}</p>
          {description && <p className="text-xs text-muted-foreground">{description}</p>}
        </div>
      </div>
      <Switch defaultChecked={defaultChecked} disabled={disabled} />
    </div>
  )
}
